import flatpickr from 'flatpickr';
import moment from 'moment';
import { DateFormatValueConverter } from '~/converters/date-format';
import { Key } from '~/utils/key';

/**
 * Props is a subset of {@link flatpicker.Options._Options}
 * It is to limit the options usages in the application.
 *
 * `enableTime` Enables time picker, default `false`
 * `enableSeconds` Enables seconds in the time picker, default false
 * `minDate` The minimum date that a user can start picking from (inclusive), default `null`
 *
 * Please add property based on {@link flatpicker.Options._Options} when necessary
 */
export interface Props {
	enableTime?: boolean;
	enableSeconds?: boolean;
	minDate?: Date;
	onChange: (value: string | undefined) => void;
}

/**
 * FlatpickrUtils allows React and Aurelia implementations of the Datepicker logic to be consistent.
 */
export class FlatpickrUtils {
	private readonly el: HTMLInputElement;

	private readonly props: Props;

	public readonly flatpickr: flatpickr.Instance;

	private altFormat: string =
		DateFormatValueConverter.toFlatpickrLocaleDateFormat();

	constructor(el: HTMLInputElement, props: Props) {
		this.el = el;
		this.props = props;

		// Mount
		this.flatpickr = flatpickr(this.el, this.buildOptions(this.props));
		this.flatpickr._input.addEventListener('keydown', this.onInputKeyDown);
		this.flatpickr._input.addEventListener('focus', this.onInputFocus);
		this.flatpickr._input.addEventListener('blur', this.onInputBlur);
		this.flatpickr.calendarContainer.addEventListener(
			'keydown',
			this.onCalendarContainerKeyDown,
		);

		this.flatpickr._input.className += ' ' + this.flatpickr.input.className;

		// NOTE(Jake): WEBUI-1015
		// This fixes a bug where opening a calendar in a modal
		// is displayed incorrectly only when its first opened.
		// This pre-calculates the calendar container size so that
		// window.pageYOffset doesn't get modified / causing inaccurate calculations.
		this.flatpickr._positionCalendar();
	}

	public unmount(): void {
		this.flatpickr._input.removeEventListener(
			'keydown',
			this.onInputKeyDown,
		);
		this.flatpickr._input.removeEventListener('focus', this.onInputFocus);
		this.flatpickr._input.removeEventListener('blur', this.onInputBlur);
		this.flatpickr.calendarContainer.removeEventListener(
			'keydown',
			this.onCalendarContainerKeyDown,
		);
		this.flatpickr.destroy();
		// NOTE: Jake: 2019-07-15 - WEBUI-2239
		// Purposefully use "any" so we can clear "flatpickr" to undefined.
		// We do this so we can still make it "readonly" to anybody using this API.
		(this as any).flatpickr = undefined;
	}

	private buildOptions(props: Props): flatpickr.Options.Options {
		const enableTimeFlag = this.props?.enableTime === true;
		const enableSecondsFlag =
			enableTimeFlag && this.props?.enableSeconds === true;

		this.altFormat =
			DateFormatValueConverter.toFlatpickrLocaleDateTimeFormat(
				enableTimeFlag,
				enableSecondsFlag,
			);

		const options: flatpickr.Options.Options = {
			enableTime: enableTimeFlag,
			enableSeconds: enableSecondsFlag,
			minuteIncrement: 1,
			time_24hr: enableSecondsFlag,
			altInput: true,
			altInputClass: '',
			altFormat: this.altFormat,
			allowInput: true,
			clickOpens: false,
			onOpen: this.onOpen,
			onChange: this.onChange,
			parseDate: this.dateParser(enableTimeFlag, enableSecondsFlag),
		};

		if (this.props?.minDate) {
			options.minDate = this.props?.minDate;
		}

		return options;
	}

	/**
	 * Customised dateParser for flatpickr.
	 * Able to parse date formats in (ddmmyyyy, dd-mm-yyyy, dd/mm/yyyy, dd.mm.yyyy)
	 * @param dateStr
	 * @returns Date of the input string or Date of (9999-01-01) if invalid string
	 */
	private dateParser(enableTime, enableSecond): (dateStr: string) => Date {
		const format = DateFormatValueConverter.toLocaleDateFormat(
			enableTime,
			enableSecond,
		);
		return (dateStr) => {
			// NOTE: if we don't provide format for moment(), it will use RFC2822 or ISO format to parse the
			// date regardless the locale.
			const parsedMoment = moment(dateStr, format);
			if (!parsedMoment.isValid()) return new Date('9999-01-01T00:00:00');
			return parsedMoment.toDate();
		};
	}

	private onOpen = (): void => {
		if (this.flatpickr) {
			// WEBUI-1307: Fixes calendar positioning issue (e.g. when opening a modal)
			this.flatpickr._positionCalendar();

			// NOTE(Jake): 2018-09-13 - WEBUI-986
			// This focuses the calendar if its not currently focused.
			//
			// This is a workaround until this PR gets merged:
			// https://github.com/flatpickr/flatpickr/pull/1503
			//
			if (
				this.flatpickr.calendarContainer &&
				!this.flatpickr.calendarContainer.contains(
					document.activeElement,
				)
			) {
				if (document.activeElement === this.flatpickr._input) {
					// NOTE(Jake): 2018-09-13 - WEBUI-986
					// This if-statement / blur() is needed, otherwise when
					// you open a picker with ArrowDown key, you can't use keyboard
					// navigation with the picker.
					this.flatpickr._input.blur();
				}

				// NOTE(Jake): 2018-09-13
				// Reset calendar back to current year/month.
				// If you clear a date field with a value like: "01/02/1981",
				// you need to reset the view or else the focus logic below
				// won't work.
				const value = this.flatpickr._input.value;
				if (!value) {
					this.flatpickr.jumpToDate(undefined);
				}

				// Focus date selected
				let dateEl: HTMLElement | undefined;
				if (value && this.flatpickr.selectedDateElem) {
					dateEl = this.flatpickr.selectedDateElem;
				} else if (this.flatpickr.todayDateElem) {
					dateEl = this.flatpickr.todayDateElem;
				}
				if (dateEl) {
					dateEl.focus();
				}
			}
		}
	};

	private onChange = (
		selectedDates: Date[],
		currentDateString: string,
		self: flatpickr.Instance,
		data?: any,
	): void => {
		switch (selectedDates.length) {
			case 0:
				this.props.onChange(undefined);
				break;
			case 1:
				// NOTE(Jake): 2018-09-11
				// Convert date to acurity format. I tried to use "selectedDates[0].toISOString();" here
				// initially but because that includes sub-second data, it wasn't working with our
				// `DateFormatValueConverter`. (ie. Passing "YYYY-MM-DDThh:mm:ss.sssZ" values doesn't work)
				const value = DateFormatValueConverter.toAcurityApiFormat(
					selectedDates[0],
				);
				this.props.onChange(value);
				break;
			default:
				// NOTE(Jake): 2018-09-07
				// Don't support date ranges currently.
				//
				// See an example implementation here:
				// https://github.com/istrau2/aurelia-flatpickr/blob/master/src/aurelia-flatpickr.js
				throw new Error('Not supported, date ranges.');
		}
	};

	private onInputKeyDown = (e: KeyboardEvent): void => {
		switch (e.key) {
			case Key.ARROW_DOWN:
				if (this.flatpickr && !this.flatpickr.isOpen) {
					e.preventDefault();
					this.flatpickr.open();
				}
				break;
			case Key.F10:
				// NOTE(York): WEBUI-1673
				// F10 applies save action in filter panel and classic form. Datepicker updates the
				// value when blurs. Trigger blur event if F10 is pressed.
				this.flatpickr._input.dispatchEvent(new Event('blur'));
				break;
		}
	};

	private onInputFocus = () => {
		if (this.flatpickr && this.flatpickr.isOpen) {
			this.flatpickr.close();
		}
	};

	// The inputValue that retrieved from _input element doesn't reflect the real user input.
	// Not sure how previous versions work, right now the inputValue retrieved are not raw value
	// anymore but a processed string by flatpickr's internal DateStringProcessor.
	private onInputBlur = () => {
		// NOTE(York): 2018-10-26 - WEBUI-939
		// `validateOnBlur` is applied on the hidden input element. When value is changed, valueChanged would be triggered and
		// value is set back to fieldModel.value with AcurityAPIFormat. Since the validation performs on fieldModel.value insteads
		// of value, so validation works.
		//
		// - Can't use this.refInputEl.blur() because this element is not focus. Have to trigger blur event manually
		// - Can't dispatch blur event immediately because value is not yet set back to fieldModel.value
		//
		// NOTE: Jake: 2019-07-22 - WEBUI-2271
		// - Remove from TaskQueue as it's not necessary.
		// - Using "new Event" instead to remove dependency on Aurelia.
		this.flatpickr.input.dispatchEvent(new Event('blur'));
	};

	private onCalendarContainerKeyDown = (e: KeyboardEvent) => {
		if (!this.flatpickr.isOpen) {
			return;
		}
		switch (e.key) {
			case Key.TAB:
				e.preventDefault();
				this.flatpickr.close();
				// NOTE(Jake): 2018-09-11
				// Tabbing from picker doesn't bring you back to
				// where you were, so we manually do this.
				//
				// Ideally this would focus the next focusable
				// element, but for now lets at least bring focus
				// back to where we were.
				this.flatpickr._input.focus();
				break;
			case Key.ESCAPE:
				e.preventDefault();
				// NOTE(Jake): 2018-09-11
				// This prevents opening the "do you want to close this"
				// popup for modals / slideout.
				e.stopImmediatePropagation();

				this.flatpickr.close();
				this.flatpickr._input.focus();
				break;
		}
	};
}
